/**
 * Política de Privacidade
 * Página pública com layout consistente do sistema
 */

import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen gradient-warm">
      {/* Header fixo com navegação */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/auth?mode=login')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para login
          </Button>
        </div>
      </header>

      {/* Conteúdo principal */}
      <main className="container max-w-4xl py-8 space-y-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="h-8 w-8 text-primary" />
              <CardTitle className="text-3xl">Política de Privacidade</CardTitle>
            </div>
            <CardDescription>
              Última atualização: 30 de outubro de 2025
            </CardDescription>
          </CardHeader>

          <Separator />

          <CardContent className="pt-6 space-y-8">
            {/* 1. Introdução */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">1.</span>
                Introdução
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  O Pudinzim Sorocaba está comprometido com a proteção da sua privacidade e dos seus dados pessoais. Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações, em total conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
                </p>
                <p>
                  Ao utilizar nosso serviço, você concorda com as práticas descritas nesta política. Caso não concorde com qualquer parte desta política, por favor, não utilize nosso sistema.
                </p>
              </div>
            </div>

            {/* 2. Dados Coletados */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">2.</span>
                Dados Coletados
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Coletamos diferentes tipos de informações para fornecer e melhorar nosso serviço:
                </p>
                
                <p className="font-medium text-foreground">2.1. Dados de Cadastro</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Nome completo</li>
                  <li>Endereço de e-mail</li>
                  <li>Senha (armazenada de forma criptografada)</li>
                  <li>Nome do estabelecimento/negócio</li>
                </ul>

                <p className="font-medium text-foreground">2.2. Dados de Uso da Plataforma</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Produtos, receitas e ingredientes cadastrados</li>
                  <li>Registros de vendas e movimentações de estoque</li>
                  <li>Informações de clientes e fornecedores</li>
                  <li>Dados de entrada de mercadorias</li>
                  <li>Configurações e preferências do sistema</li>
                </ul>

                <p className="font-medium text-foreground">2.3. Dados Técnicos e de Navegação</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Endereço IP</li>
                  <li>Tipo e versão do navegador</li>
                  <li>Sistema operacional</li>
                  <li>Páginas visitadas e tempo de permanência</li>
                  <li>Ações realizadas no sistema (logs de auditoria)</li>
                </ul>

                <p className="font-medium text-foreground">2.4. Dados de Pagamento</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Informações de assinatura e plano contratado</li>
                  <li>Histórico de pagamentos (processados por gateway seguro)</li>
                </ul>
                <p className="text-sm italic">
                  Nota: Dados de cartão de crédito são processados diretamente por nossos parceiros de pagamento certificados (Stripe) e nunca são armazenados em nossos servidores.
                </p>
              </div>
            </div>

            {/* 3. Uso dos Dados */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">3.</span>
                Como Utilizamos Seus Dados
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Utilizamos os dados coletados para as seguintes finalidades:
                </p>

                <p className="font-medium text-foreground">3.1. Prestação do Serviço</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Criar e gerenciar sua conta de usuário</li>
                  <li>Fornecer acesso às funcionalidades do sistema</li>
                  <li>Processar e armazenar os dados do seu negócio</li>
                  <li>Gerar relatórios e análises personalizadas</li>
                </ul>

                <p className="font-medium text-foreground">3.2. Comunicação</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Enviar notificações importantes sobre o serviço</li>
                  <li>Responder solicitações de suporte técnico</li>
                  <li>Informar sobre atualizações e novas funcionalidades</li>
                  <li>Enviar avisos de cobrança e renovação de assinatura</li>
                </ul>

                <p className="font-medium text-foreground">3.3. Melhorias e Segurança</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Analisar padrões de uso para melhorar a plataforma</li>
                  <li>Identificar e corrigir problemas técnicos</li>
                  <li>Detectar e prevenir fraudes ou uso indevido</li>
                  <li>Garantir a segurança da plataforma e dos dados</li>
                </ul>

                <p className="font-medium text-foreground">3.4. Conformidade Legal</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Cumprir obrigações legais e regulatórias</li>
                  <li>Atender solicitações de autoridades competentes</li>
                  <li>Proteger direitos e propriedade do Pudinzim Sorocaba</li>
                </ul>
              </div>
            </div>

            {/* 4. Compartilhamento de Dados */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">4.</span>
                Compartilhamento de Dados
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  O Pudinzim Sorocaba <strong>NÃO vende, aluga ou comercializa</strong> seus dados pessoais ou comerciais com terceiros.
                </p>
                <p>
                  Seus dados podem ser compartilhados apenas nas seguintes situações específicas:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li><strong>Prestadores de serviço:</strong> Empresas que nos auxiliam na operação da plataforma (hospedagem, processamento de pagamentos, envio de e-mails), sempre sob rigorosos contratos de confidencialidade</li>
                  <li><strong>Obrigações legais:</strong> Quando exigido por lei, ordem judicial ou autoridade competente</li>
                  <li><strong>Proteção de direitos:</strong> Para proteger nossos direitos, segurança ou propriedade, ou de nossos usuários</li>
                  <li><strong>Com seu consentimento:</strong> Em outras situações, somente com sua autorização expressa</li>
                </ul>
                <p>
                  Nossos prestadores de serviço atuais incluem:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li><strong>Supabase:</strong> Banco de dados e autenticação</li>
                  <li><strong>Stripe:</strong> Processamento de pagamentos</li>
                  <li><strong>Lovable Cloud:</strong> Hospedagem da aplicação</li>
                </ul>
              </div>
            </div>

            {/* 5. Segurança dos Dados */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">5.</span>
                Segurança dos Dados
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Implementamos medidas técnicas e organizacionais rigorosas para proteger seus dados:
                </p>

                <p className="font-medium text-foreground">Medidas Técnicas:</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Criptografia SSL/TLS para transmissão de dados</li>
                  <li>Senhas armazenadas com hash bcrypt (nunca em texto plano)</li>
                  <li>Banco de dados com controle de acesso por Row Level Security (RLS)</li>
                  <li>Backups automáticos regulares</li>
                  <li>Monitoramento contínuo de segurança</li>
                  <li>Proteção contra ataques CSRF, XSS e SQL Injection</li>
                </ul>

                <p className="font-medium text-foreground">Medidas Organizacionais:</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Acesso aos dados restrito a pessoal autorizado</li>
                  <li>Auditorias regulares de segurança</li>
                  <li>Política de segurança da informação</li>
                  <li>Treinamento da equipe em proteção de dados</li>
                </ul>

                <p>
                  Apesar de nossos melhores esforços, nenhum sistema é 100% seguro. Recomendamos que você também tome precauções, como usar senhas fortes e únicas, e não compartilhar suas credenciais.
                </p>
              </div>
            </div>

            {/* 6. Cookies e Armazenamento Local */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">6.</span>
                Cookies e Armazenamento Local
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Utilizamos cookies e armazenamento local do navegador para melhorar sua experiência:
                </p>

                <p className="font-medium text-foreground">Cookies Essenciais:</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Token de autenticação (manter você conectado)</li>
                  <li>Preferências de tema (claro/escuro)</li>
                  <li>Configurações de interface</li>
                </ul>

                <p className="font-medium text-foreground">Armazenamento Local:</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Cache de dados para funcionar offline (PWA)</li>
                  <li>Preferências de visualização</li>
                  <li>Dados temporários de formulários</li>
                </ul>

                <p>
                  Você pode desabilitar cookies nas configurações do seu navegador, mas isso pode afetar o funcionamento de algumas funcionalidades do sistema.
                </p>
              </div>
            </div>

            {/* 7. Seus Direitos (LGPD) */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">7.</span>
                Seus Direitos (LGPD)
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  De acordo com a LGPD, você tem os seguintes direitos sobre seus dados pessoais:
                </p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li><strong>Confirmação e acesso:</strong> Saber se tratamos seus dados e acessá-los</li>
                  <li><strong>Correção:</strong> Solicitar a correção de dados incompletos ou desatualizados</li>
                  <li><strong>Anonimização ou exclusão:</strong> Solicitar a exclusão de dados desnecessários ou tratados em desconformidade</li>
                  <li><strong>Portabilidade:</strong> Solicitar a transferência de seus dados a outro fornecedor</li>
                  <li><strong>Informação sobre compartilhamento:</strong> Saber com quem compartilhamos seus dados</li>
                  <li><strong>Revogação de consentimento:</strong> Retirar seu consentimento a qualquer momento</li>
                  <li><strong>Oposição:</strong> Se opor ao tratamento de dados em determinadas situações</li>
                </ul>
                <p>
                  Para exercer qualquer desses direitos, entre em contato através do e-mail <strong>privacidade@pudinzimsorocaba.com.br</strong> ou através do menu "Configurações" no sistema.
                </p>
                <p>
                  Responderemos sua solicitação em até 15 dias úteis, conforme estabelecido pela LGPD.
                </p>
              </div>
            </div>

            {/* 8. Retenção de Dados */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">8.</span>
                Retenção de Dados
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Mantemos seus dados pelo tempo necessário para fornecer o serviço e cumprir obrigações legais:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li><strong>Dados de conta ativa:</strong> Durante todo o período de uso do serviço</li>
                  <li><strong>Dados de transações:</strong> Por até 5 anos (obrigação legal contábil e tributária)</li>
                  <li><strong>Logs de acesso:</strong> Por até 6 meses (Marco Civil da Internet)</li>
                  <li><strong>Dados de conta cancelada:</strong> Por até 90 dias para permitir reativação, após isso são anonimizados ou excluídos</li>
                </ul>
                <p>
                  Após o período de retenção, os dados são excluídos permanentemente ou anonimizados de forma irreversível.
                </p>
              </div>
            </div>

            {/* 9. Alterações nesta Política */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">9.</span>
                Alterações nesta Política
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossas práticas ou na legislação. Quando fizermos alterações significativas, notificaremos você através de:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>E-mail para o endereço cadastrado</li>
                  <li>Aviso destacado na plataforma</li>
                  <li>Atualização da data "Última atualização" no topo desta página</li>
                </ul>
                <p>
                  Recomendamos que você revise esta política regularmente para se manter informado sobre como protegemos seus dados.
                </p>
              </div>
            </div>

            {/* 10. Contato */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">10.</span>
                Contato e Encarregado de Dados (DPO)
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Para dúvidas, solicitações ou questões relacionadas à privacidade e proteção de dados:
                </p>
                <ul className="list-none space-y-2 pl-0">
                  <li><strong>E-mail de Privacidade:</strong> privacidade@pudinzimsorocaba.com.br</li>
                  <li><strong>E-mail Geral:</strong> contato@pudinzimsorocaba.com.br</li>
                  <li><strong>Sistema:</strong> Menu "Configurações" → "Privacidade e Dados"</li>
                </ul>
                <p>
                  Nosso Encarregado de Proteção de Dados (DPO) está disponível para esclarecer dúvidas e auxiliar no exercício dos seus direitos previstos na LGPD.
                </p>
                <p>
                  Tempo de resposta: até 15 dias úteis conforme LGPD.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t py-6 mt-12">
        <div className="container max-w-4xl text-center text-sm text-muted-foreground">
          © 2025 Pudinzim Sorocaba. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}
